import { FC, useEffect, useRef } from 'react';
import { Control, UseFormSetValue, useWatch } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';

import { CrossIcon } from '../../../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../../../components/app-formatted-message/index';
import Button from '../../../../../../../components/button/index';
import { ModalWrapper } from '../../../../../../../components/modal-wrapper/index';
import { DocumentPreview } from '../../../../../templates/components/forms/map-fields-modal/document/document-preview';
import { useQuickSignSignatureSettings } from '../../../../hooks/use-quick-sign-signature-settings.hook';
import {
  QuickSignFormSchema,
  QuickSignSignatureTab,
} from '../../../../validation/quick-sign.validation';
import { QuickSignSignatureFieldsSidebar } from './quick-sign-signature-fields-sidebar';

type QuickSignSignatureSettingsModalProps = {
  control: Control<QuickSignFormSchema>;
  isOpen: boolean;
  onClose: () => void;
  setValue: UseFormSetValue<QuickSignFormSchema>;
};

export const QuickSignSignatureSettingsModal: FC<QuickSignSignatureSettingsModalProps> = ({
  control,
  isOpen,
  onClose,
  setValue,
}) => {
  const shouldRollbackOnCloseRef = useRef(true);
  const tabs = useWatch({
    control,
    name: 'stepFour.tabs',
    defaultValue: [],
  }) as QuickSignSignatureTab[];

  const { docxPreviewBuffer, file, sessionRef } = useQuickSignSignatureSettings({
    control,
    isOpen,
    setValue,
  });

  const canSave = tabs.length === 0 || tabs.every((tab) => tab.type != null && tab.value != null);

  useEffect(() => {
    if (isOpen) {
      shouldRollbackOnCloseRef.current = true;
    }
  }, [isOpen]);

  const handleDismissWithRollback = () => {
    if (shouldRollbackOnCloseRef.current) {
      setValue('stepFour.tabs', sessionRef.current.tabs, { shouldValidate: true });
    }
    shouldRollbackOnCloseRef.current = true;
    onClose();
  };

  const handleSave = () => {
    if (!canSave) return;
    shouldRollbackOnCloseRef.current = false;
    setValue('stepFour.signatureSettingsCompleted', true, { shouldValidate: true });
    onClose();
  };

  return (
    <ModalWrapper
      className={twMerge(
        '!bottom-[25px] !left-[66px] !right-[66px] !top-[25px] !z-[61] flex !h-auto !w-auto !max-w-none !translate-x-0 !translate-y-0 flex-col overflow-hidden rounded-xl bg-white shadow-lg',
      )}
      control={{
        open: isOpen,
        onOpenChange: (open: boolean) => {
          if (!open) handleDismissWithRollback();
        },
      }}
      onBgClick={handleDismissWithRollback}
      onInteractOutside={(e) => {
        if (e.target instanceof Element && e.target.closest('[role="listbox"]')) {
          e.preventDefault();
        }
      }}
      overlayClassName="z-[60]"
      side="CENTER"
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="mb-4 mt-6 flex h-8 shrink-0 items-center justify-between px-6">
          <h2 className="text-[20px] font-semibold text-gray-700">
            <AppFormattedMessage id="SIGNATURE_SETTINGS" />
          </h2>
          <Button
            className="h-8 w-8 rounded-full border border-[#F2F2F2] p-0 shadow-[0_0_18.667px_0_rgba(0,0,0,0.08)]"
            onClick={handleDismissWithRollback}
            styleType="NONE"
            type="button"
          >
            <CrossIcon className="h-4 w-4" iconColor="#344054" />
          </Button>
        </div>

        <div className="mx-6 flex min-h-0 flex-col gap-4 rounded-t-md bg-gray-50 p-4 px-6">
          <div className="flex min-h-0 flex-1 gap-4 overflow-visible pb-1">
            <QuickSignSignatureFieldsSidebar control={control} setValue={setValue} />
            <DocumentPreview
              docxArrayBuffer={docxPreviewBuffer ?? undefined}
              fileName={file?.name}
              hideVariableHighlighting
            />
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-between bg-gray-100 px-6 py-4">
          <Button
            className="px-4 py-2 text-sm font-medium text-gray-700"
            onClick={handleDismissWithRollback}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="CANCEL" />
          </Button>
          <Button
            className="bg-brand-700 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            disabled={!canSave}
            onClick={handleSave}
            styleType="DEFAULT_ROUNDED"
            type="button"
          >
            <AppFormattedMessage id="SAVE_SETTINGS" />
          </Button>
        </div>
      </div>
    </ModalWrapper>
  );
};
